/**
 * @file
 * @fileOverview This file contains the light components for the Aframe scene.
 * @version 1.0.0
 * @author Parker Eli Bidigare
 * @date 2024-05-26
 */

import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

AFRAME.registerComponent("render-order", {
    schema: {
      order: { type: "number", default: 0 },
    },
    init: function () {
      this.el.addEventListener("object3dset", () => {
        const obj = this.el.getObject3D("mesh");
        if (obj) {
          obj.renderOrder = this.data.order;
          obj.traverse((node) => {
            if (node.isMesh) {
              node.renderOrder = this.data.order;
            }
          });
        }
      });
    },
  });

  AFRAME.registerComponent("transparent-fix", {
    init: function () {
      this.el.addEventListener("model-loaded", () => {
        const obj = this.el.getObject3D("mesh");
        if (obj) {
          obj.traverse((node) => {
            if (node.isMesh) {
              if (
                node.material.name.toLowerCase().includes("glass") ||
                node.material.name.toLowerCase().includes("window")
              ) {
                node.material.transparent = true;
                node.material.opacity = 0.6;
                node.material.refractionRatio = 0.98;
                //node.material.envMap = this.el.sceneEl.components.renderer.renderer.environment;
                node.material.needsUpdate = true;
              }
            }
          });
        }
      });
    },
  });

  AFRAME.registerShader("custom-material", {
    schema: {
      color: { type: "color", is: "uniform" },
      roughness: { type: "number", default: 0.5, is: "uniform" },
      metalness: { type: "number", default: 0.5, is: "uniform" },
      normalMap: { type: "map", is: "uniform" },
      envMap: { type: "map", is: "uniform" },
    },
  
    vertexShader: `
      varying vec3 vWorldPosition;
      varying vec3 vNormal;
      varying vec2 vUv;
  
      void main() {
        vUv = uv;
        vNormal = normal;
        vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
  
    fragmentShader: `
      uniform vec3 color;
      uniform float roughness;
      uniform float metalness;
      uniform sampler2D normalMap;
      uniform samplerCube envMap;
  
      varying vec3 vWorldPosition;
      varying vec3 vNormal;
      varying vec2 vUv;
  
      void main() {
        vec3 normal = texture2D(normalMap, vUv).rgb * 2.0 - 1.0;
        vec3 viewDir = normalize(cameraPosition - vWorldPosition);
        vec3 reflectDir = reflect(-viewDir, normal);
        
        vec3 envColor = textureCube(envMap, reflectDir).rgb;
        vec3 diffuse = color;
        vec3 specular = mix(vec3(0.04), diffuse, metalness);
        
        vec3 finalColor = mix(diffuse, envColor, roughness * metalness);
        
        gl_FragColor = vec4(finalColor, 1.0);
      }
    `,
  });







  AFRAME.registerComponent('fbx-model-loader-6', {
    schema: {
      src: {type: 'string'},
      mtlSrc: {type: 'string'},
      crossOrigin: {default: 'anonymous'},
      texturePath: {type: 'string', default: ''},
      envMap: {type: 'asset'},
      checkUV: {type: 'boolean', default: true},
      fixUV: {type: 'boolean', default: true}
    },
  
    init: function () {
      var el = this.el;
      var data = this.data;
  
      this.loadMTL(data.mtlSrc).then((materials) => {
        this.loadFBX(data.src, materials);
      }).catch(error => {
        console.error('Error loading MTL:', error);
      });
    },
  
    loadMTL: function(mtlUrl) {
      return fetch(mtlUrl)
        .then(response => response.text())
        .then(mtlContent => this.parseMTL(mtlContent));
    },
  
    parseMTL: function(mtlContent) {
      const materials = {};
      const lines = mtlContent.split('\n');
      let currentMaterial = null;
  
      for (const line of lines) {
        const parts = line.trim().split(/\s+/);
        if (parts[0] === 'newmtl') {
          currentMaterial = parts[1];
          materials[currentMaterial] = {};
        } else if (currentMaterial) {
          switch (parts[0]) {
            case 'map_Kd':
              materials[currentMaterial].baseColor = parts.slice(1).join(' ');
              break;
            case 'map_Bump':
            case 'bump':
              materials[currentMaterial].normal = parts.slice(1).join(' ');
              break;
            case 'map_Ns':
              materials[currentMaterial].roughness = parts.slice(1).join(' ');
              break;
            case 'refl':
            case 'map_Pm':
              materials[currentMaterial].metallic = parts.slice(1).join(' ');
              break;
            case 'map_Ka':
              materials[currentMaterial].aoMap = parts.slice(1).join(' ');
              break;
            case 'map_d':
              materials[currentMaterial].alphaMap = parts.slice(1).join(' ');
              break;
            case 'Kd':
              materials[currentMaterial].color = parts.slice(1).join(' ');
              break;
          }
        }
      }
      return materials;
    },
  
    loadFBX: function(fbxUrl, materials) {
      var el = this.el;
      var data = this.data;
  
      var loader = new THREE.FBXLoader();
      loader.setCrossOrigin(data.crossOrigin);
  
      loader.load(fbxUrl, (object) => {
        object.traverse((child) => {
          if (child.isMesh) {
            const materialName = child.material.name;
            if (materials[materialName]) {
              const pbrMaterial = this.createPBRMaterial(materials[materialName]);
              child.material = pbrMaterial;
  
              if (data.checkUV) {
                this.checkAndFixUVs(child);
              }
            }
          }
        });
  
        if (object.animations && object.animations.length) {
          var mixer = new THREE.AnimationMixer(object);
          var action = mixer.clipAction(object.animations[0]);
          action.play();
  
          el.sceneEl.addEventListener('render-target-loaded', function () {
            el.sceneEl.renderer.setAnimationLoop(function (time) {
              mixer.update(time / 1000);
            });
          });
        }
  
        el.setObject3D('mesh', object);
        el.emit('model-loaded', {format: 'fbx', model: object});
      }, undefined, (error) => {
        console.error('Error loading FBX:', error);
      });
    },
  
    createPBRMaterial: function(materialData) {
      const material = new THREE.MeshStandardMaterial();
  
      if (materialData.color) {
        const color = materialData.color.split(' ').map(Number);
        material.color.setRGB(color[0], color[1], color[2]);
      }
  
      if (materialData.baseColor) {
        this.loadTexture(material, 'map', materialData.baseColor);
      }
      if (materialData.normal) {
        this.loadTexture(material, 'normalMap', materialData.normal);
      }
      if (materialData.roughness) {
        this.loadTexture(material, 'roughnessMap', materialData.roughness);
      }
      if (materialData.metallic) {
        this.loadTexture(material, 'metalnessMap', materialData.metallic);
      }
      if (materialData.aoMap) {
        this.loadTexture(material, 'aoMap', materialData.aoMap);
      }
      if (materialData.alphaMap) {
        this.loadTexture(material, 'alphaMap', materialData.alphaMap);
        material.transparent = true;
      }
  
      if (this.data.envMap) {
        material.envMap = new THREE.TextureLoader().load(this.data.envMap);
        material.envMapIntensity = 1;
      }
  
      return material;
    },
  
    loadTexture: function(material, mapType, texturePath) {
      new THREE.TextureLoader().load(
        this.data.texturePath + texturePath,
        (texture) => {
          material[mapType] = texture;
          if (mapType === 'map') {
            material.color.setRGB(1, 1, 1);
          }
          material.needsUpdate = true;
        },
        undefined,
        (error) => {
          console.error('Error loading texture:', this.data.texturePath + texturePath, error);
        }
      );
    },
  
    checkAndFixUVs: function(mesh) {
      const geometry = mesh.geometry;
      const uv = geometry.attributes.uv;
  
      if (!uv) {
        console.warn('Mesh has no UV coordinates:', mesh.name);
        return;
      }
  
      let needsUpdate = false;
      const uvArray = uv.array;
  
      for (let i = 0; i < uvArray.length; i += 2) {
        let u = uvArray[i];
        let v = uvArray[i + 1];
  
        if (u < 0 || u > 1 || v < 0 || v > 1) {
          console.warn('Out-of-range UV detected in mesh:', mesh.name);
          
          if (this.data.fixUV) {
            uvArray[i] = Math.max(0, Math.min(1, u));
            uvArray[i + 1] = Math.max(0, Math.min(1, v));
            needsUpdate = true;
          }
        }
      }
  
      if (needsUpdate) {
        uv.needsUpdate = true;
        console.log('UV coordinates fixed for mesh:', mesh.name);
      }
  
      if (geometry.index) {
        const index = geometry.index.array;
        for (let i = 0; i < index.length; i += 3) {
          const a = index[i] * 2;
          const b = index[i + 1] * 2;
          const c = index[i + 2] * 2;
  
          const area = this.calculateUVTriangleArea(
            uvArray[a], uvArray[a + 1],
            uvArray[b], uvArray[b + 1],
            uvArray[c], uvArray[c + 1]
          );
  
          if (area < 1e-6) {
            console.warn('Degenerate UV triangle detected in mesh:', mesh.name);
          }
        }
      }
    },
  
    calculateUVTriangleArea: function(x1, y1, x2, y2, x3, y3) {
      return Math.abs((x1 * (y2 - y3) + x2 * (y3 - y1) + x3 * (y1 - y2)) / 2);
    },
  
    remove: function () {
      if (this.el.getObject3D('mesh')) {
        this.el.removeObject3D('mesh');
      }
    }
  });






  AFRAME.registerComponent('fbx-model-loader-5', {
    schema: {
      src: {type: 'string'},
      mtlSrc: {type: 'string'},
      crossOrigin: {default: 'anonymous'},
      texturePath: {type: 'string', default: ''},
      envMap: {type: 'asset'}, // Optional environment map for reflections
    },
  
    init: function () {
      var el = this.el;
      var data = this.data;
  
      this.loadMTL(data.mtlSrc).then((materials) => {
        this.loadFBX(data.src, materials);
      }).catch(error => {
        console.error('Error loading MTL:', error);
      });
    },
  
    loadMTL: function(mtlUrl) {
      return fetch(mtlUrl)
        .then(response => response.text())
        .then(mtlContent => this.parseMTL(mtlContent));
    },
  
    parseMTL: function(mtlContent) {
      const materials = {};
      const lines = mtlContent.split('\n');
      let currentMaterial = null;
  
      for (const line of lines) {
        const parts = line.trim().split(/\s+/);
        if (parts[0] === 'newmtl') {
          currentMaterial = parts[1];
          materials[currentMaterial] = {};
        } else if (currentMaterial) {
          switch (parts[0]) {
            case 'map_Kd':
              materials[currentMaterial].baseColor = parts.slice(1).join(' ');
              break;
            case 'map_Bump':
            case 'bump':
              materials[currentMaterial].normal = parts.slice(1).join(' ');
              break;
            case 'map_Ns':
              materials[currentMaterial].roughness = parts.slice(1).join(' ');
              break;
            case 'refl':
            case 'map_Pm':
              materials[currentMaterial].metallic = parts.slice(1).join(' ');
              break;
            case 'map_Ka':
              materials[currentMaterial].aoMap = parts.slice(1).join(' ');
              break;
            case 'map_d':
              materials[currentMaterial].alphaMap = parts.slice(1).join(' ');
              break;
            case 'Kd':
              materials[currentMaterial].color = parts.slice(1).join(' ');
              break;
          }
        }
      }
      return materials;
    },
  
    loadFBX: function(fbxUrl, materials) {
      var el = this.el;
      var data = this.data;
  
      var loader = new THREE.FBXLoader();
      loader.setCrossOrigin(data.crossOrigin);
  
      loader.load(fbxUrl, (object) => {
        object.traverse((child) => {
          if (child.isMesh) {
            const materialName = child.material.name;
            if (materials[materialName]) {
              const pbrMaterial = this.createPBRMaterial(materials[materialName]);
              child.material = pbrMaterial;
            }
          }
        });
  
        if (object.animations && object.animations.length) {
          var mixer = new THREE.AnimationMixer(object);
          var action = mixer.clipAction(object.animations[0]);
          action.play();
  
          el.sceneEl.addEventListener('render-target-loaded', function () {
            el.sceneEl.renderer.setAnimationLoop(function (time) {
              mixer.update(time / 1000);
            });
          });
        }
  
        el.setObject3D('mesh', object);
        el.emit('model-loaded', {format: 'fbx', model: object});
      }, undefined, (error) => {
        console.error('Error loading FBX:', error);
      });
    },
  
    createPBRMaterial: function(materialData) {
      const material = new THREE.MeshStandardMaterial();
  
      if (materialData.color) {
        const color = materialData.color.split(' ').map(Number);
        material.color.setRGB(color[0], color[1], color[2]);
      }
  
      if (materialData.baseColor) {
        this.loadTexture(material, 'map', materialData.baseColor);
      }
      if (materialData.normal) {
        this.loadTexture(material, 'normalMap', materialData.normal);
      }
      if (materialData.roughness) {
        this.loadTexture(material, 'roughnessMap', materialData.roughness);
      }
      if (materialData.metallic) {
        this.loadTexture(material, 'metalnessMap', materialData.metallic);
      }
      if (materialData.aoMap) {
        this.loadTexture(material, 'aoMap', materialData.aoMap);
      }
      if (materialData.alphaMap) {
        this.loadTexture(material, 'alphaMap', materialData.alphaMap);
        material.transparent = true;
      }
  
      // Apply environment map if available
      if (this.data.envMap) {
        material.envMap = new THREE.TextureLoader().load(this.data.envMap);
        material.envMapIntensity = 1;
      }
  
      return material;
    },
  
    loadTexture: function(material, mapType, texturePath) {
      new THREE.TextureLoader().load(
        this.data.texturePath + texturePath,
        (texture) => {
          material[mapType] = texture;
          if (mapType === 'map') {
            material.color.setRGB(1, 1, 1); // Reset color to white when using a base texture
          }
          material.needsUpdate = true;
        },
        undefined,
        (error) => {
          console.error('Error loading texture:', this.data.texturePath + texturePath, error);
        }
      );
    },
  
    remove: function () {
      if (this.el.getObject3D('mesh')) {
        this.el.removeObject3D('mesh');
      }
    }
  });


  AFRAME.registerComponent('grass-2', {
    schema: {
      width: {type: 'number', default: 10},
      depth: {type: 'number', default: 10},
      density: {type: 'number', default: 5000},
      bladeHeight: {type: 'number', default: 0.3},
      bladeWidth: {type: 'number', default: 0.02},
      bladeDepth: {type: 'number', default: 0.01},
      color: {type: 'color', default: '#3a3'},
      windStrength: {type: 'number', default: 0.1},
      shadowQuality: {type: 'string', default: 'medium', oneOf: ['low', 'medium', 'high']}
    },
  
    init: function () {
      this.createGrass();
      this.setupLighting();
    },
  
    setupLighting: function() {
      const scene = this.el.sceneEl;
      if (!scene.getAttribute('shadow')) {
        scene.setAttribute('shadow', 'type: pcfsoft');
      }
  
      // Check if a directional light for shadows already exists
      let directionalLight = scene.querySelector('[light="type: directional"][light="castShadow: true"]');
      if (!directionalLight) {
        // Add directional light for shadows if it doesn't exist
        directionalLight = document.createElement('a-entity');
        directionalLight.setAttribute('light', 'type: directional; castShadow: true; intensity: 0.5; shadowMapWidth: 1024; shadowMapHeight: 1024');
        directionalLight.setAttribute('position', '1 1 1');
        scene.appendChild(directionalLight);
      }
    },
  
    createGrass: function () {
      const { width, depth, density, bladeHeight, bladeWidth, bladeDepth, color, windStrength, shadowQuality } = this.data;
  
      const bladeGeometry = this.createBladeGeometry(bladeHeight, bladeWidth, bladeDepth);
  
      const geometry = new THREE.InstancedBufferGeometry();
      geometry.index = bladeGeometry.index;
      geometry.attributes = bladeGeometry.attributes;
  
      const instances = density;
      const translateArray = new Float32Array(instances * 3);
      const scaleArray = new Float32Array(instances);
      const rotateArray = new Float32Array(instances);
      const shadowCastArray = new Float32Array(instances);
  
      let shadowCastThreshold;
      switch(shadowQuality) {
        case 'low': shadowCastThreshold = 0.95; break;
        case 'medium': shadowCastThreshold = 0.8; break;
        case 'high': shadowCastThreshold = 0.6; break;
      }
  
      for (let i = 0; i < instances; i++) {
        translateArray[i * 3] = (Math.random() - 0.5) * width;
        translateArray[i * 3 + 1] = 0;
        translateArray[i * 3 + 2] = (Math.random() - 0.5) * depth;
        scaleArray[i] = 0.5 + Math.random() * 0.5;
        rotateArray[i] = Math.random() * Math.PI * 2;
        shadowCastArray[i] = Math.random() > shadowCastThreshold ? 1 : 0;
      }
  
      geometry.setAttribute('translate', new THREE.InstancedBufferAttribute(translateArray, 3));
      geometry.setAttribute('scale', new THREE.InstancedBufferAttribute(scaleArray, 1));
      geometry.setAttribute('rotate', new THREE.InstancedBufferAttribute(rotateArray, 1));
      geometry.setAttribute('shadowCast', new THREE.InstancedBufferAttribute(shadowCastArray, 1));
  
      const material = new THREE.ShaderMaterial({
        vertexShader: `
          attribute vec3 translate;
          attribute float scale;
          attribute float rotate;
          attribute float shadowCast;
  
          uniform float time;
          uniform float windStrength;
          varying vec2 vUv;
          varying float vHeight;
          varying float vShadowCast;
  
          void main() {
            vUv = uv;
            vHeight = position.y;
            vShadowCast = shadowCast;
            
            vec3 pos = position * scale;
            pos = vec3(
              pos.x * cos(rotate) - pos.z * sin(rotate),
              pos.y,
              pos.x * sin(rotate) + pos.z * cos(rotate)
            );
            pos += translate;
            
            float wave = sin(translate.x * 0.5 + time * 2.0) * cos(translate.z * 0.5 + time * 2.0);
            float heightFactor = smoothstep(0.0, 1.0, position.y);
            pos.xz += wave * windStrength * heightFactor;
            
            gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
          }
        `,
        fragmentShader: `
          uniform vec3 color;
          varying vec2 vUv;
          varying float vHeight;
          varying float vShadowCast;
  
          void main() {
            vec3 grassColor = mix(color * 0.5, color, smoothstep(0.0, 0.7, vHeight));
            float alpha = mix(0.7, 1.0, vHeight);
            
            gl_FragColor = vec4(grassColor, alpha);
  
            if (vShadowCast < 0.5) {
              gl_FragColor.a *= 0.5;  // Make non-shadow-casting blades more transparent
            }
          }
        `,
        uniforms: {
          color: { value: new THREE.Color(color) },
          time: { value: 0 },
          windStrength: { value: windStrength }
        },
        side: THREE.DoubleSide,
        transparent: true,
        alphaTest: 0.5,
        depthWrite: false
      });
  
      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = false;
      this.el.setObject3D('mesh', mesh);
  
      const animate = () => {
        material.uniforms.time.value = performance.now() * 0.001;
        requestAnimationFrame(animate);
      };
      animate();
    },
  
    createBladeGeometry: function(height, width, depth) {
      const shape = new THREE.Shape();
      shape.moveTo(0, 0);
      shape.lineTo(-width / 2, 0);
      shape.quadraticCurveTo(0, height, width / 2, 0);
      shape.lineTo(0, 0);
  
      const extrudeSettings = {
        steps: 1,
        depth: depth,
        bevelEnabled: false
      };
  
      return new THREE.ExtrudeGeometry(shape, extrudeSettings);
    }
  });

  const PerlinNoise = {
    grad3: [[1,1,0],[-1,1,0],[1,-1,0],[-1,-1,0],[1,0,1],[-1,0,1],[1,0,-1],[-1,0,-1],[0,1,1],[0,-1,1],[0,1,-1],[0,-1,-1]],
    p: [...Array(256)].map(() => Math.floor(Math.random() * 256)),
    fade: t => t * t * t * (t * (t * 6 - 15) + 10),
    lerp: (t, a, b) => a + t * (b - a),
    grad: function(hash, x, y, z) {
      const h = hash & 15, u = h < 8 ? x : y, v = h < 4 ? y : h === 12 || h === 14 ? x : z;
      return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
    },
    noise: function(x, y, z) {
      const X = Math.floor(x) & 255, Y = Math.floor(y) & 255, Z = Math.floor(z) & 255;
      x -= Math.floor(x); y -= Math.floor(y); z -= Math.floor(z);
      const u = this.fade(x), v = this.fade(y), w = this.fade(z);
      const A = this.p[X] + Y, AA = this.p[A] + Z, AB = this.p[A + 1] + Z,
            B = this.p[X + 1] + Y, BA = this.p[B] + Z, BB = this.p[B + 1] + Z;
      return this.lerp(w, this.lerp(v, this.lerp(u, this.grad(this.p[AA], x, y, z),
        this.grad(this.p[BA], x - 1, y, z)),
        this.lerp(u, this.grad(this.p[AB], x, y - 1, z),
        this.grad(this.p[BB], x - 1, y - 1, z))),
        this.lerp(v, this.lerp(u, this.grad(this.p[AA + 1], x, y, z - 1),
        this.grad(this.p[BA + 1], x - 1, y, z - 1)),
        this.lerp(u, this.grad(this.p[AB + 1], x, y - 1, z - 1),
        this.grad(this.p[BB + 1], x - 1, y - 1, z - 1))));
    }
  };
  
  AFRAME.registerComponent('random-tree-mesh', {
    schema: {
      trunkHeight: {type: 'number', default: 5},
      trunkRadius: {type: 'number', default: 0.5},
      branchLevels: {type: 'number', default: 3},
      branchFactor: {type: 'number', default: 3},
      branchAngle: {type: 'number', default: 45},
      segmentsRadial: {type: 'number', default: 8},
      segmentsHeight: {type: 'number', default: 10},
      leafSize: {type: 'number', default: 0.2},
      leafDensity: {type: 'number', default: 3},
      noiseStrength: {type: 'number', default: 0.1},
      windStrength: {type: 'number', default: 0.1},
      windSpeed: {type: 'number', default: 1},
      barkTexture: {type: 'string'},
      leafTexture: {type: 'string'},
      instanceCount: {type: 'number', default: 1},
      spread: {type: 'number', default: 10},
      debug: {type: 'boolean', default: true}
    },
  
    init: function() {
      this.log('Initializing random-tree-mesh component');
      this.treeMeshes = [];
      this.loadTextures().then(() => {
        this.log('Textures loaded successfully');
        this.generateTreeTemplate();
        this.createInstances();
        this.setupWindAnimation();
      }).catch(error => {
        console.error('Error in random-tree-mesh initialization:', error);
      });
    },
  
    log: function(message) {
      if (this.data.debug) {
        console.log(`[random-tree-mesh] ${message}`);
      }
    },
  
    loadTextures: function() {
      this.log('Loading textures');
      return new Promise((resolve, reject) => {
        const textureLoader = new THREE.TextureLoader();
        const loadTexture = (textureSrc) => {
          if (textureSrc.startsWith('#')) {
            const imgEl = document.querySelector(textureSrc);
            return imgEl ? imgEl.src : '';
          }
          return textureSrc;
        };
  
        const barkTextureSrc = loadTexture(this.data.barkTexture);
        const leafTextureSrc = loadTexture(this.data.leafTexture);
  
        this.log(`Loading bark texture from: ${barkTextureSrc}`);
        this.log(`Loading leaf texture from: ${leafTextureSrc}`);
  
        this.barkTexture = textureLoader.load(barkTextureSrc, () => {
          this.log('Bark texture loaded');
          this.leafTexture = textureLoader.load(leafTextureSrc, () => {
            this.log('Leaf texture loaded');
            this.barkTexture.wrapS = this.barkTexture.wrapT = THREE.RepeatWrapping;
            this.leafTexture.wrapS = this.leafTexture.wrapT = THREE.RepeatWrapping;
            resolve();
          }, undefined, reject);
        }, undefined, reject);
      });
    },
  
    generateTreeTemplate: function() {
      this.log('Generating tree template');
      const geometry = new THREE.BufferGeometry();
      const positions = [];
      const normals = [];
      const uvs = [];
      const indices = [];
      const colors = [];
      this.originalPositions = [];
  
      this.generateTrunk(positions, normals, uvs, indices, colors);
      this.generateBranches(positions, normals, uvs, indices, colors, 0, 0, this.data.trunkHeight, this.data.trunkRadius, new THREE.Vector3(0, 1, 0));
  
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
      geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
      geometry.setIndex(indices);
  
      this.originalPositions = [...positions];
      this.treeGeometry = geometry;
      this.log(`Tree template generated with ${positions.length / 3} vertices`);
    },
  
    generateTrunk: function(positions, normals, uvs, indices, colors) {
      const {trunkHeight, trunkRadius, segmentsRadial, segmentsHeight, noiseStrength} = this.data;
      const trunkColor = new THREE.Color(this.data.trunkColor);
  
      for (let y = 0; y <= segmentsHeight; y++) {
        const v = y / segmentsHeight;
        const radius = trunkRadius * (1 - v * 0.5);
  
        for (let x = 0; x <= segmentsRadial; x++) {
          const u = x / segmentsRadial;
          const theta = u * Math.PI * 2;
          const sinTheta = Math.sin(theta);
          const cosTheta = Math.cos(theta);
  
          const noise = PerlinNoise.noise(sinTheta, v, cosTheta) * noiseStrength;
          const px = (radius + noise) * sinTheta;
          const py = y * trunkHeight / segmentsHeight + noise;
          const pz = (radius + noise) * cosTheta;
  
          positions.push(px, py, pz);
          normals.push(sinTheta, 0, cosTheta);
          uvs.push(u, v);
          colors.push(trunkColor.r, trunkColor.g, trunkColor.b);
  
          if (y < segmentsHeight && x < segmentsRadial) {
            const a = y * (segmentsRadial + 1) + x;
            const b = a + segmentsRadial + 1;
            indices.push(a, b, a + 1, b, b + 1, a + 1);
          }
        }
      }
    },
  
    generateBranches: function(positions, normals, uvs, indices, colors, startIndex, level, startY, parentRadius, parentDirection) {
      if (level >= this.data.branchLevels) return startIndex;
  
      const {branchFactor, segmentsRadial, segmentsHeight, noiseStrength, branchAngle} = this.data;
      const branchColor = new THREE.Color(this.data.branchColor);
      const branchLength = parentRadius * 3;
      const branchRadius = parentRadius * 0.7;
  
      for (let i = 0; i < branchFactor; i++) {
        const angle = (i / branchFactor) * Math.PI * 2;
        const x = Math.cos(angle) * parentRadius;
        const z = Math.sin(angle) * parentRadius;
  
        const branchDirection = new THREE.Vector3(
          Math.sin(angle) * Math.sin(branchAngle * Math.PI / 180),
          Math.cos(branchAngle * Math.PI / 180),
          Math.cos(angle) * Math.sin(branchAngle * Math.PI / 180)
        ).normalize();
  
        const rotationAxis = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), parentDirection).normalize();
        const rotationAngle = Math.acos(parentDirection.y);
        branchDirection.applyAxisAngle(rotationAxis, rotationAngle);
  
        for (let y = 0; y <= segmentsHeight; y++) {
          const v = y / segmentsHeight;
          const radius = branchRadius * (1 - v * 0.5);
  
          for (let x = 0; x <= segmentsRadial; x++) {
            const u = x / segmentsRadial;
            const theta = u * Math.PI * 2;
            const sinTheta = Math.sin(theta);
            const cosTheta = Math.cos(theta);
  
            const noise = PerlinNoise.noise(sinTheta, v, cosTheta) * noiseStrength;
            let bx = (radius + noise) * sinTheta;
            let by = y * branchLength / segmentsHeight + noise;
            let bz = (radius + noise) * cosTheta;
  
            const pos = new THREE.Vector3(bx, by, bz);
            pos.applyAxisAngle(new THREE.Vector3(0, 0, 1), Math.atan2(branchDirection.x, branchDirection.y));
            pos.applyAxisAngle(new THREE.Vector3(1, 0, 0), Math.asin(branchDirection.z));
  
            const fx = pos.x + x;
            const fy = pos.y + startY;
            const fz = pos.z + z;
  
            positions.push(fx, fy, fz);
            normals.push(sinTheta, 0, cosTheta);
            uvs.push(u, v);
            colors.push(branchColor.r, branchColor.g, branchColor.b);
  
            if (y < segmentsHeight && x < segmentsRadial) {
              const a = startIndex + y * (segmentsRadial + 1) + x;
              const b = a + segmentsRadial + 1;
              indices.push(a, b, a + 1, b, b + 1, a + 1);
            }
          }
        }
  
        startIndex += (segmentsHeight + 1) * (segmentsRadial + 1);
  
        const endPoint = new THREE.Vector3(x, startY + branchLength, z);
        startIndex = this.generateBranches(positions, normals, uvs, indices, colors, startIndex, level + 1, startY + branchLength, branchRadius, branchDirection);
  
        if (level === this.data.branchLevels - 1) {
          startIndex = this.generateLeaves(positions, normals, uvs, indices, colors, startIndex, endPoint, branchDirection);
        }
      }
  
      return startIndex;
    },
  
    generateLeaves: function(positions, normals, uvs, indices, colors, startIndex, branchEnd, branchDirection) {
      const leafColor = new THREE.Color(this.data.leafColor);
      const leafSize = this.data.leafSize;
      const leafDensity = this.data.leafDensity;
  
      for (let i = 0; i < leafDensity; i++) {
        const u = Math.random(), v = Math.random(), w = Math.random();
  
        const leafPosition = new THREE.Vector3(
          branchEnd.x + (u - 0.5) * leafSize,
          branchEnd.y + (v - 0.5) * leafSize,
          branchEnd.z + (w - 0.5) * leafSize
        );
  
        const leafNormal = new THREE.Vector3(u - 0.5, 1, w - 0.5).normalize();
        leafNormal.applyAxisAngle(new THREE.Vector3(0, 0, 1), Math.atan2(branchDirection.x, branchDirection.y));
        leafNormal.applyAxisAngle(new THREE.Vector3(1, 0, 0), Math.asin(branchDirection.z));
  
        const leafTangent = new THREE.Vector3().crossVectors(leafNormal, new THREE.Vector3(0, 1, 0)).normalize();
        const leafBitangent = new THREE.Vector3().crossVectors(leafNormal, leafTangent);
  
        const leafVertices = [
          new THREE.Vector3(-0.5, 0, -0.5),
          new THREE.Vector3(0.5, 0, -0.5),
          new THREE.Vector3(0.5, 0, 0.5),
          new THREE.Vector3(-0.5, 0, 0.5)
        ];
  
        for (const vertex of leafVertices) {
          vertex.multiplyScalar(leafSize);
          vertex.applyMatrix3(new THREE.Matrix3().setFromMatrix4(new THREE.Matrix4().makeBasis(leafTangent, leafNormal, leafBitangent)));
          vertex.add(leafPosition);
  
          positions.push(vertex.x, vertex.y, vertex.z);
          normals.push(leafNormal.x, leafNormal.y, leafNormal.z);
          uvs.push(vertex.x < 0 ? 0 : 1, vertex.z < 0 ? 0 : 1);
          colors.push(leafColor.r, leafColor.g, leafColor.b);
        }
  
        indices.push(
          startIndex, startIndex + 1, startIndex + 2,
          startIndex, startIndex + 2, startIndex + 3
        );
  
        startIndex += 4;
      }
  
      return startIndex;
    },
  
    createInstances: function() {
      this.log('Creating tree instances');
      const instancedGeometry = new THREE.InstancedBufferGeometry().copy(this.treeGeometry);
  
      const instanceCount = this.data.instanceCount;
      const spread = this.data.spread;
  
      const offsets = new Float32Array(instanceCount * 3);
      const scales = new Float32Array(instanceCount);
  
      for (let i = 0; i < instanceCount; i++) {
        offsets[i * 3] = (Math.random() - 0.5) * spread;
        offsets[i * 3 + 1] = 0;
        offsets[i * 3 + 2] = (Math.random() - 0.5) * spread;
        scales[i] = 0.8 + Math.random() * 0.4;
      }
  
      instancedGeometry.setAttribute('offset', new THREE.InstancedBufferAttribute(offsets, 3));
      instancedGeometry.setAttribute('scale', new THREE.InstancedBufferAttribute(scales, 1));
  
      const barkMaterial = new THREE.MeshStandardMaterial({
        map: this.barkTexture,
        vertexColors: true,
        side: THREE.DoubleSide,
        roughness: 0.8,
        metalness: 0.2
      });
  
      const leafMaterial = new THREE.MeshStandardMaterial({
        map: this.leafTexture,
        vertexColors: true,
        side: THREE.DoubleSide,
        transparent: true,
        alphaTest: 0.5,
        roughness: 0.7,
        metalness: 0.1
      });
  
      const instancedMaterial = [barkMaterial, leafMaterial];
  
      const instancedMesh = new THREE.InstancedMesh(instancedGeometry, instancedMaterial, instanceCount);
      instancedMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  
      this.log(`Created instanced mesh with ${this.data.instanceCount} instances`);
      
      if (!this.el.object3D) {
        console.error('No object3D found on the entity');
        return;
      }
      
      this.el.setObject3D('mesh', instancedMesh);
      this.treeMesh = instancedMesh;
      
      this.log(`Tree mesh visible: ${this.treeMesh.visible}`);
      this.log(`Tree mesh position: ${JSON.stringify(this.treeMesh.position)}`);
      this.log(`Tree mesh scale: ${JSON.stringify(this.treeMesh.scale)}`);
    },
    
    setupWindAnimation: function() {
      this.windTime = 0;
      this.el.sceneEl.addBehavior(this);
    },
  
  
    tick: function(time, timeDelta) {
      if (!this.treeMesh || !this.treeMesh.geometry) {
        this.log('Tree mesh or geometry not ready yet');
        return;
      }
      this.windTime += timeDelta * 0.001 * this.data.windSpeed;
      this.updateWindAnimation();
    },
  
  
    updateWindAnimation: function() {
      if (!this.treeMesh || !this.treeMesh.geometry) {
        this.log('Tree mesh or geometry not available for wind animation');
        return;
      }
  
      const {windStrength, instanceCount} = this.data;
      const positions = this.treeMesh.geometry.attributes.position.array;
      const offsets = this.treeMesh.geometry.attributes.offset.array;
      const scales = this.treeMesh.geometry.attributes.scale.array;
  
      if (!positions || !offsets || !scales || !this.originalPositions) {
        this.log('Required attributes not available for wind animation');
        return;
      }
  
      for (let i = 0; i < positions.length; i += 3) {
        const yPosition = this.originalPositions[i + 1];
        const windFactor = Math.pow(yPosition / this.data.trunkHeight, 2);
  
        for (let j = 0; j < instanceCount; j++) {
          const offsetX = offsets[j * 3];
          const offsetZ = offsets[j * 3 + 2];
          const scale = scales[j];
  
          const windNoise = PerlinNoise.noise(
            this.windTime + (positions[i] + offsetX) * 0.1,
            this.windTime + (positions[i + 1]) * 0.1,
            this.windTime + (positions[i + 2] + offsetZ) * 0.1
          );
  
          positions[i] = this.originalPositions[i] + windNoise * windStrength * windFactor * scale;
          positions[i + 2] = this.originalPositions[i + 2] + windNoise * windStrength * windFactor * scale;
        }
      }
  
      this.treeMesh.geometry.attributes.position.needsUpdate = true;
      this.treeMesh.instanceMatrix.needsUpdate = true;
    },
  
    remove: function() {
      if (this.treeMesh) {
        this.el.removeObject3D('mesh');
      }
      this.el.sceneEl.removeBehavior(this);
    }
  
  });
    


  
  AFRAME.registerComponent('rock-generator', {
    schema: {
      baseUrl: { type: 'string', default: '' },
      radius: { type: 'number', default: 1 },
      detail: { type: 'int', default: 3 },
      noise: { type: 'number', default: 0.5 },
      color: { type: 'color', default: '#888888' },
      textureUrl: { type: 'string', default: '' },
      textureRepeat: { type: 'vec2', default: {x: 1, y: 1} },
      normalMapUrl: { type: 'string', default: '' },
      aoMapUrl: { type: 'string', default: '' },
      roughnessMapUrl: { type: 'string', default: '' },
      displacementMapUrl: { type: 'string', default: '' },
      displacementScale: { type: 'number', default: 0.1 },
      displacementBias: { type: 'number', default: 0 }
    },
  
    init: function() {
      this.generateRock();
    },
  
    update: function() {
      this.generateRock();
    },
  
    generateRock: function() {
      if (this.mesh) {
        this.el.removeObject3D('mesh');
      }
  
      // Start with a more irregular shape
      let geometry = new THREE.DodecahedronGeometry(this.data.radius, this.data.detail);
      
      // Ensure the geometry is indexed
      if (!geometry.index) {
        geometry = mergeVertices(geometry);
      }
  
      // Apply noise
      this.applyNoise(geometry);
  
      // Create material
      const material = new THREE.MeshStandardMaterial({
        color: this.data.color,
        side: THREE.DoubleSide
      });
  
      // Load and set textures
      this.loadTexture(this.data.textureUrl, 'map', material);
      this.loadTexture(this.data.normalMapUrl, 'normalMap', material);
      this.loadTexture(this.data.aoMapUrl, 'aoMap', material);
      this.loadTexture(this.data.roughnessMapUrl, 'roughnessMap', material);
      this.loadTexture(this.data.displacementMapUrl, 'displacementMap', material);
  
      if (this.data.displacementMapUrl) {
        material.displacementScale = this.data.displacementScale;
        material.displacementBias = this.data.displacementBias;
      }
  
      // Set up UV coordinates for aoMap
      if (this.data.aoMapUrl && geometry.attributes.uv) {
        geometry.setAttribute('uv2', new THREE.BufferAttribute(geometry.attributes.uv.array, 2));
      }
  
      this.mesh = new THREE.Mesh(geometry, material);
      this.el.setObject3D('mesh', this.mesh);
  
      // Debug: Log geometry details
      console.log('Geometry vertices:', geometry.attributes.position.count);
      console.log('Geometry faces:', geometry.index ? geometry.index.count / 3 : 'N/A');
    },
  
    applyNoise: function(geometry) {
      const positionAttribute = geometry.getAttribute('position');
      const vertex = new THREE.Vector3();
  
      for (let i = 0; i < positionAttribute.count; i++) {
        vertex.fromBufferAttribute(positionAttribute, i);
        const distance = vertex.length();
        const noise = this.noise3D(vertex.x * 2, vertex.y * 2, vertex.z * 2) * this.data.noise;
        
        // Apply noise more aggressively to create a more jagged shape
        vertex.normalize().multiplyScalar(distance * (1 + noise * 2));
        
        // Occasionally create sharp edges or flat surfaces
        if (Math.random() > 0.7) {
          vertex.multiplyScalar(0.8 + Math.random() * 0.4);
        }
  
        positionAttribute.setXYZ(i, vertex.x, vertex.y, vertex.z);
      }
  
      geometry.computeVertexNormals();
    },
  
    loadTexture: function(url, mapType, material) {
      if (url) {
        const fullUrl = this.getFullUrl(url);
        new THREE.TextureLoader().load(
          fullUrl,
          (texture) => {
            texture.repeat.set(this.data.textureRepeat.x, this.data.textureRepeat.y);
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
            
            // Handle PNG transparency
            if (fullUrl.toLowerCase().endsWith('.png')) {
              texture.premultiplyAlpha = true;
              material.transparent = true;
              material.alphaTest = 0.5;
            }
            
            material[mapType] = texture;
            material.needsUpdate = true;
            console.log(`Texture loaded successfully: ${mapType}`);
          },
          undefined,
          (err) => {
            console.error('Error loading texture:', fullUrl, err);
          }
        );
      }
    },
  
    getFullUrl: function(url) {
      if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) {
        return url;
      } else {
        return this.data.baseUrl + url;
      }
    },
  
    noise3D: function(x, y, z) {
      // Improved noise function for more varied results
      return (Math.sin(x*0.8 + y*0.3 + z*0.5) * 0.5 +
              Math.sin(x*0.2 + y*0.8 + z*0.3) * 0.3 +
              Math.sin(x*0.7 + y*0.5 + z*0.8) * 0.2) * 0.5 + 0.5;
    },
  
    remove: function() {
      if (this.mesh) {
        this.el.removeObject3D('mesh');
      }
    }
  });


  AFRAME.registerSystem('pbr-shader', {
    schema: {
      displacementScale: {type: 'number', default: 0.1} 
    },

    init: function() {
      const vertexShader = `
        #include <common>
        #include <shadowmap_pars_vertex>
        #include <uv_pars_vertex>

        // Varyings
        varying vec2 vUv;
        varying vec3 vNormal;
        varying vec3 vWorldPosition;
        varying vec3 vViewPosition;

        // Uniforms for displacement
        uniform sampler2D displacementMap;
        uniform float displacementScale;

        void main() {
          // Handle UV
          #include <uv_vertex>
          vUv = uv;

          // Calculate normal manually
          vec3 objectNormal = normal;
          vec3 transformedNormal = normalMatrix * objectNormal;
          vNormal = normalize(transformedNormal);

          // Always sample the displacement map. If no texture is assigned,
          // Three.js passes a placeholder (black), so disp = 0.0 effectively.
          float disp = texture2D(displacementMap, uv).r * displacementScale;

          // Offset position along vertex normal
          vec3 displacedPosition = position + normal * disp;

          // World pos
          vec4 worldPosition = modelMatrix * vec4(displacedPosition, 1.0);
          vWorldPosition = worldPosition.xyz;

          // View pos
          vec4 mvPosition = modelViewMatrix * vec4(displacedPosition, 1.0);
          vViewPosition = -mvPosition.xyz;

          // Final clip-space position
          gl_Position = projectionMatrix * mvPosition;

          // Shadow
          #include <shadowmap_vertex>
        }
      `;

      const fragmentShader = `
        #include <common>
        #include <packing>
        #include <lights_pars_begin>
        #include <shadowmap_pars_fragment>
        #include <shadowmask_pars_fragment>

        // Varyings
        varying vec2 vUv;
        varying vec3 vNormal;
        varying vec3 vWorldPosition;
        varying vec3 vViewPosition;

        // Core texture uniforms
        uniform sampler2D diffuseMap;
        uniform sampler2D normalMap;
        uniform sampler2D roughnessMap;
        uniform sampler2D metalnessMap;
        uniform sampler2D aoMap;

        // Additional texture uniforms
        uniform sampler2D cavityMap;
        uniform sampler2D anisotropyMap;
        uniform sampler2D emissiveMap;
        uniform sampler2D sheenMap;

        // Already in vertex: uniform sampler2D displacementMap;

        // Rename PI to avoid collisions with #include <common>
        const float MY_PI = 3.14159265359;

        // --- PBR Functions ---

        float DistributionGGX(vec3 N, vec3 H, float roughness, float anisotropy) {
          // A quick hack: reduce roughness in one direction if anisotropy > 0
          // True anisotropic shading is more involved. This is a simplistic approach.
          float a  = roughness * roughness;
          float a2 = a * a;

          // If anisotropy > 0, slightly reduce a2 to simulate "stretch".
          a2 = mix(a2, a2 * 0.5, clamp(anisotropy, 0.0, 1.0));

          float NdotH = max(dot(N, H), 0.0);
          float NdotH2 = NdotH * NdotH;

          float nom   = a2;
          float denom = (NdotH2 * (a2 - 1.0) + 1.0);
          denom = MY_PI * denom * denom;

          return nom / denom;
        }

        float GeometrySchlickGGX(float NdotV, float roughness) {
          float r = (roughness + 1.0);
          float k = (r * r) / 8.0;

          float nom   = NdotV;
          float denom = NdotV * (1.0 - k) + k;
          return nom / denom;
        }

        float GeometrySmith(vec3 N, vec3 V, vec3 L, float roughness) {
          float NdotV = max(dot(N, V), 0.0);
          float NdotL = max(dot(N, L), 0.0);
          float ggx2 = GeometrySchlickGGX(NdotV, roughness);
          float ggx1 = GeometrySchlickGGX(NdotL, roughness);
          return ggx1 * ggx2;
        }

        vec3 fresnelSchlick(float cosTheta, vec3 F0) {
          return F0 + (1.0 - F0) * pow(clamp(1.0 - cosTheta, 0.0, 1.0), 5.0);
        }

        void main() {
          // Sample main textures
          vec4 diffuseColor = texture2D(diffuseMap, vUv);
          float roughness   = texture2D(roughnessMap, vUv).r;
          float metalness   = texture2D(metalnessMap, vUv).r;
          float ao          = texture2D(aoMap, vUv).r;

          // Always sample cavity map. If none is assigned, it's black => cavity=0 => no effect.
          float cavity = texture2D(cavityMap, vUv).r;
          // Combine it with AO by multiplying (1.0 for white => no change)
          ao *= cavity;

          // Always sample anisotropy. If none is assigned => black => aniso=0 => no anisotropy.
          float aniso = texture2D(anisotropyMap, vUv).r;

          // Always sample emissive. If none is assigned => black => no glow.
          vec3 emissive = texture2D(emissiveMap, vUv).rgb;

          // Always sample sheen. If none is assigned => black => no sheen.
          vec3 sheenColor = texture2D(sheenMap, vUv).rgb;

          // Basic vectors
          vec3 N = normalize(vNormal);
          vec3 V = normalize(vViewPosition);

          // For a typical metalness workflow, start with F0=0.04
          // Then mix with diffuse color if metalness=1
          vec3 F0 = vec3(0.04);
          F0 = mix(F0, diffuseColor.rgb, metalness);

          // Accumulate direct lighting
          vec3 Lo = vec3(0.0);

          for (int i = 0; i < NUM_DIR_LIGHTS; i++) {
            vec3 L = normalize(directionalLights[i].direction);
            vec3 H = normalize(V + L);
            vec3 radiance = directionalLights[i].color;

            // Cook-Torrance BRDF
            float NDF = DistributionGGX(N, H, roughness, aniso);
            float G   = GeometrySmith(N, V, L, roughness);
            vec3  F   = fresnelSchlick(max(dot(H, V), 0.0), F0);

            vec3 numerator    = NDF * G * F;
            float denominator = 4.0 * max(dot(N, V), 0.0) * max(dot(N, L), 0.0) + 0.0001;
            vec3 specular = numerator / denominator;

            // kS = Fresnel
            vec3 kS = F;
            // kD = (1 - kS) * (1 - metalness)
            vec3 kD = (vec3(1.0) - kS) * (1.0 - metalness);

            float NdotL = max(dot(N, L), 0.0);

            float shadow = getShadowMask();
            Lo += (kD * diffuseColor.rgb / MY_PI + specular) * radiance * NdotL * shadow;
          }

          // Ambient + AO
          vec3 ambient = vec3(0.03) * diffuseColor.rgb * ao;
          // Combine direct + ambient
          vec3 color = ambient + Lo;

          // Simple SHEEN
          float NdotV = max(dot(N, V), 0.0);
          float sheenFactor = pow(1.0 - NdotV, 5.0);
          color += sheenColor * sheenFactor;

          // Add emissive glow
          color += emissive;

          // Simple tonemapping + gamma
          color = color / (color + vec3(1.0));
          color = pow(color, vec3(1.0 / 2.2));

          gl_FragColor = vec4(color, diffuseColor.a);
        }
      `;

      // Build the material with new uniforms
      const material = new THREE.ShaderMaterial({
        uniforms: THREE.UniformsUtils.merge([
          THREE.UniformsLib.lights,
          THREE.UniformsLib.shadowmap,
          {
            // Existing
            diffuseMap:   { value: null },
            normalMap:    { value: null },
            roughnessMap: { value: null },
            metalnessMap: { value: null },
            aoMap:        { value: null },

            // New
            displacementMap: { value: null },
            cavityMap:       { value: null },
            anisotropyMap:   { value: null },
            emissiveMap:     { value: null },
            sheenMap:        { value: null },

            // Displacement scale
            displacementScale: { value: this.data.displacementScale }
          }
        ]),
        vertexShader: vertexShader,
        fragmentShader: fragmentShader,
        lights: true
      });

      material.shadowMap = true;
      material.extensions = {
        derivatives: true,
        drawBuffers: true,
        fragDepth: true
      };

      this.material = material;
    }
  });

  // --- PBR Component ---
  AFRAME.registerComponent('pbr-material', {
    schema: {
      diffuseMap:       { type: 'selector' },
      normalMap:        { type: 'selector' },
      roughnessMap:     { type: 'selector' },
      metalnessMap:     { type: 'selector' },
      aoMap:            { type: 'selector' },
      displacementMap:  { type: 'selector' },
      cavityMap:        { type: 'selector' },
      anisotropyMap:    { type: 'selector' },
      emissiveMap:      { type: 'selector' },
      sheenMap:         { type: 'selector' },

      // Expose a displacement scale you can adjust in HTML
      displacementScale: { type: 'number', default: 0.1 }
    },

    init: function() {
      this.system = this.el.sceneEl.systems['pbr-shader'];
      this.material = this.system.material.clone();

      // Bind texture update method
      this.updateTexture = this.updateTexture.bind(this);

      // Listen for image "load" so we can create textures
      Object.keys(this.data).forEach(key => {
        const val = this.data[key];
        if (val && val.tagName === 'IMG') {
          val.addEventListener('load', () => this.updateTexture(key));
        }
      });

      // Apply the material once the model is ready
      this.el.addEventListener('model-loaded', () => this.applyMaterial());
    },

    update: function(oldData) {
      // Update numeric uniforms (like displacementScale)
      this.material.uniforms.displacementScale.value = this.data.displacementScale;

      // Rebind any changed textures
      Object.keys(this.data).forEach(key => {
        if (key === 'displacementScale') { return; }  // skip numeric
        if (oldData && this.data[key] !== oldData[key]) {
          // Remove old
          if (oldData[key] && oldData[key].removeEventListener) {
            oldData[key].removeEventListener('load', this.updateTexture);
          }
          // Add new
          const newVal = this.data[key];
          if (newVal && newVal.tagName === 'IMG') {
            newVal.addEventListener('load', () => this.updateTexture(key));
            this.updateTexture(key);
          }
        }
      });
    },

    updateTexture: function(key) {
      const img = this.data[key];
      if (!img) return;

      // Create and update texture
      const texture = new THREE.Texture(img);
      texture.needsUpdate = true;

      // Wrap + filter settings
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.minFilter = THREE.LinearMipMapLinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.generateMipmaps = true;

      // Update the uniform
      this.material.uniforms[key].value = texture;

      // Reapply material so changes take effect
      this.applyMaterial();
    },

    applyMaterial: function() {
      const mesh = this.el.getObject3D('mesh');
      if (mesh) {
        mesh.material = this.material;

        // If we use an AO or cavity map, ensure uv2 exists
        if (this.data.aoMap || this.data.cavityMap) {
          if (!mesh.geometry.attributes.uv2) {
            mesh.geometry.attributes.uv2 = mesh.geometry.attributes.uv;
          }
        }
      }
    },

    remove: function() {
      // Clean up event listeners
      Object.keys(this.data).forEach(key => {
        const val = this.data[key];
        if (val && val.removeEventListener) {
          val.removeEventListener('load', this.updateTexture);
        }
      });

      // Dispose textures
      Object.keys(this.material.uniforms).forEach(key => {
        const texture = this.material.uniforms[key].value;
        if (texture && texture.dispose) {
          texture.dispose();
        }
      });

      // Finally dispose of the material
      if (this.material) {
        this.material.dispose();
      }
    }
  });




  AFRAME.registerComponent('shadow-material', {
    init: function () {
        this.el.addEventListener('model-loaded', () => {
            const obj = this.el.getObject3D('mesh');
            if (obj) {
                obj.traverse((node) => {
                    if (node.isMesh) {
                        node.castShadow = true;
                        node.receiveShadow = true;
                        if (node.material) {
                            node.material.needsUpdate = true;
                        }
                    }
                });
            }
        });
    }
});



// Interior Enhanced Shader Component for A-Frame
// This shader enhances GLB models for interior scenes with improved lighting and materials

AFRAME.registerShader('interior-enhanced', {
  schema: {
    // Texture maps
    map: {type: 'map'},
    normalMap: {type: 'map'},
    roughnessMap: {type: 'map'},
    metalnessMap: {type: 'map'},
    aoMap: {type: 'map'},
    emissiveMap: {type: 'map'},
    envMap: {type: 'map'},
    
    // Base properties
    color: {type: 'color', default: '#ffffff'},
    roughness: {default: 0.5},
    metalness: {default: 0.3},
    emissive: {type: 'color', default: '#000000'},
    emissiveIntensity: {default: 1.0},
    
    // Lighting controls
    lightIntensity: {default: 1.0},
    ambientLightColor: {type: 'color', default: '#404040'},
    ambientIntensity: {default: 0.5},
    
    // Environment and reflections
    envMapIntensity: {default: 1.0},
    reflectivity: {default: 0.3},
    
    // Additional effects
    aoMapIntensity: {default: 1.0},
    normalScale: {type: 'vec2', default: {x: 1, y: 1}},
    
    // Transparency
    transparent: {default: false},
    opacity: {default: 1.0},
    
    // Advanced PBR
    clearcoat: {default: 0.0},
    clearcoatRoughness: {default: 0.0},
    ior: {default: 1.5},
    transmission: {default: 0.0},
    
    // Shadows
    receiveShadow: {default: true}
  },
  
  /**
   * Vertex shader for the interior-enhanced material
   * Handles normal mapping, position calculations, and lighting preparation
   */
  vertexShader: `
    // Varying variables passed to the fragment shader
    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vPosition;
    varying vec3 vWorldPosition;
    varying vec3 vViewPosition;
    
    #ifdef USE_NORMALMAP
    varying vec3 vTangent;
    varying vec3 vBitangent;
    #endif
    
    #ifdef USE_AOMAP
    attribute vec2 uv2;
    varying vec2 vUv2;
    #endif
    
    void main() {
      // Pass UV coordinates to fragment shader
      vUv = uv;
      
      // Calculate normal in view space for lighting
      vNormal = normalize(normalMatrix * normal);
      
      // Setup for normal mapping
      #ifdef USE_NORMALMAP
        vTangent = normalize(normalMatrix * tangent.xyz);
        vBitangent = normalize(cross(vNormal, vTangent) * tangent.w);
      #endif
      
      // Position calculations
      vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
      vPosition = mvPosition.xyz;
      vViewPosition = -mvPosition.xyz;
      
      // World position for environment mapping and lighting
      vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
      
      // Additional UV set for ambient occlusion
      #ifdef USE_AOMAP
        vUv2 = uv2;
      #endif
      
      // Final position
      gl_Position = projectionMatrix * mvPosition;
    }
  `,
  
  /**
   * Fragment shader for the interior-enhanced material
   * Implements physically-based rendering with focus on interior lighting
   */
  fragmentShader: `
    // Precision declaration
    precision highp float;
    
    // Uniforms from schema
    uniform sampler2D map;
    uniform sampler2D normalMap;
    uniform sampler2D roughnessMap;
    uniform sampler2D metalnessMap;
    uniform sampler2D aoMap;
    uniform samplerCube envMap;
    uniform sampler2D emissiveMap;
    
    uniform vec3 color;
    uniform float roughness;
    uniform float metalness;
    uniform vec3 emissive;
    uniform float emissiveIntensity;
    
    uniform float lightIntensity;
    uniform vec3 ambientLightColor;
    uniform float ambientIntensity;
    
    uniform float envMapIntensity;
    uniform float reflectivity;
    
    uniform float aoMapIntensity;
    uniform vec2 normalScale;
    
    uniform bool transparent;
    uniform float opacity;
    
    uniform float clearcoat;
    uniform float clearcoatRoughness;
    uniform float ior;
    uniform float transmission;
    
    uniform bool receiveShadow;
    
    // Varying variables from vertex shader
    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vPosition;
    varying vec3 vWorldPosition;
    varying vec3 vViewPosition;
    
    #ifdef USE_NORMALMAP
    varying vec3 vTangent;
    varying vec3 vBitangent;
    #endif
    
    #ifdef USE_AOMAP
    varying vec2 vUv2;
    #endif
    
    // Constants
    const float PI = 3.14159265359;
    
    // Utility functions for PBR lighting
    
    // Schlick approximation for Fresnel
    vec3 F_Schlick(vec3 f0, float cosTheta) {
      return f0 + (vec3(1.0) - f0) * pow(1.0 - cosTheta, 5.0);
    }
    
    // GGX/Trowbridge-Reitz normal distribution function
    float D_GGX(float NoH, float roughness) {
      float alpha = roughness * roughness;
      float alpha2 = alpha * alpha;
      float NoH2 = NoH * NoH;
      float denom = NoH2 * (alpha2 - 1.0) + 1.0;
      return alpha2 / (PI * denom * denom);
    }
    
    // Smith's method with GGX for geometry shadowing
    float G_Smith(float NoV, float NoL, float roughness) {
      float alpha = roughness * roughness;
      float k = alpha / 2.0;
      float G1_L = NoL / (NoL * (1.0 - k) + k);
      float G1_V = NoV / (NoV * (1.0 - k) + k);
      return G1_L * G1_V;
    }
    
    // Tone mapping function (ACES approximation)
    vec3 toneMapping(vec3 color) {
      const float a = 2.51;
      const float b = 0.03;
      const float c = 2.43;
      const float d = 0.59;
      const float e = 0.14;
      return clamp((color * (a * color + b)) / (color * (c * color + d) + e), 0.0, 1.0);
    }
    
    void main() {
      // Get base color from texture or uniform
      vec4 texColor = texture2D(map, vUv);
      vec3 baseColor = texColor.rgb * color;
      float alpha = texColor.a * opacity;
      
      // Early discard for fully transparent pixels
      if (transparent && alpha < 0.01) discard;
      
      // Calculate normal from normal map or use geometric normal
      vec3 normal = normalize(vNormal);
      
      #ifdef USE_NORMALMAP
        vec3 normalFromMap = texture2D(normalMap, vUv).rgb * 2.0 - 1.0;
        normalFromMap.xy *= normalScale;
        
        // Construct TBN matrix for normal mapping
        mat3 tbn = mat3(normalize(vTangent), normalize(vBitangent), normal);
        normal = normalize(tbn * normalFromMap);
      #endif
      
      // Get material properties from maps or uniform values
      float roughnessValue = roughnessMap ? texture2D(roughnessMap, vUv).r : roughness;
      float metalnessValue = metalnessMap ? texture2D(metalnessMap, vUv).r : metalness;
      
      // Apply ambient occlusion
      float ao = 1.0;
      #ifdef USE_AOMAP
        ao = texture2D(aoMap, vUv2).r;
        ao = mix(1.0, ao, aoMapIntensity);
      #endif
      
      // Get emissive contribution
      vec3 emissiveColor = emissive;
      #ifdef USE_EMISSIVEMAP
        emissiveColor *= texture2D(emissiveMap, vUv).rgb;
      #endif
      emissiveColor *= emissiveIntensity;
      
      // Setup vectors for lighting calculations
      vec3 viewDir = normalize(vViewPosition);
      vec3 N = normal;
      vec3 V = viewDir;
      
      // Calculate F0 (fresnel reflectance at normal incidence)
      vec3 F0 = mix(vec3(0.04), baseColor, metalnessValue);
      
      // Setup main lighting parameters
      // In a real implementation, these would come from scene lights
      vec3 mainLightDir = normalize(vec3(1.0, 1.0, 1.0));
      vec3 mainLightColor = vec3(1.0, 0.9, 0.8) * lightIntensity;
      
      // Add some interior-specific fill lights
      vec3 fillLight1Dir = normalize(vec3(-1.0, 0.5, 0.5));
      vec3 fillLight1Color = vec3(0.4, 0.5, 0.6) * lightIntensity * 0.5;
      
      vec3 fillLight2Dir = normalize(vec3(0.5, 0.2, -1.0));
      vec3 fillLight2Color = vec3(0.5, 0.4, 0.3) * lightIntensity * 0.3;
      
      // Ambient contribution for interiors (slightly blue-tinted)
      vec3 ambient = ambientLightColor * ambientIntensity * baseColor;
      
      // Initialize lighting accumulator
      vec3 finalColor = ambient * ao;
      
      // Process each light source
      for (int i = 0; i < 3; i++) {
        vec3 lightDir;
        vec3 lightColor;
        
        // Select light source based on iteration
        if (i == 0) {
          lightDir = mainLightDir;
          lightColor = mainLightColor;
        } else if (i == 1) {
          lightDir = fillLight1Dir;
          lightColor = fillLight1Color;
        } else {
          lightDir = fillLight2Dir;
          lightColor = fillLight2Color;
        }
        
        // Light vector
        vec3 L = lightDir;
        
        // Half vector between view and light
        vec3 H = normalize(V + L);
        
        // Various dot products needed for lighting
        float NdotL = max(dot(N, L), 0.0);
        float NdotV = max(dot(N, V), 0.001);
        float NdotH = max(dot(N, H), 0.0);
        float VdotH = max(dot(V, H), 0.0);
        
        if (NdotL > 0.0) {
          // Specular reflection (Cook-Torrance BRDF)
          float D = D_GGX(NdotH, roughnessValue);
          vec3 F = F_Schlick(F0, VdotH);
          float G = G_Smith(NdotV, NdotL, roughnessValue);
          
          vec3 specular = (D * F * G) / (4.0 * NdotV * NdotL + 0.001);
          
          // Diffuse contribution (Lambert)
          vec3 diffuse = (1.0 - F) * (1.0 - metalnessValue) * baseColor / PI;
          
          // Add to final color
          finalColor += (diffuse + specular) * lightColor * NdotL;
        }
      }
      
      // Add environment reflections
      #ifdef USE_ENVMAP
        vec3 worldNormal = normalize(mat3(viewMatrix) * normal);
        vec3 reflectVec = reflect(normalize(vWorldPosition - cameraPosition), worldNormal);
        
        // Sample environment map with roughness-based LOD
        float envMapMipLevel = roughnessValue * 7.0; // Assuming 8 mip levels
        vec3 envMapColor = textureCube(envMap, reflectVec, envMapMipLevel).rgb;
        
        // Fresnel for environment reflections
        vec3 envFresnel = F_Schlick(F0, NdotV);
        
        // Add environment reflection
        finalColor += envMapColor * envFresnel * envMapIntensity * ao;
      #endif
      
      // Add emissive contribution
      finalColor += emissiveColor;
      
      // Apply clearcoat if enabled
      if (clearcoat > 0.0) {
        float clearcoatNdotL = max(dot(normal, mainLightDir), 0.0);
        float clearcoatNdotH = max(dot(normal, normalize(viewDir + mainLightDir)), 0.0);
        float clearcoatNdotV = max(dot(normal, viewDir), 0.001);
        
        float Dc = D_GGX(clearcoatNdotH, clearcoatRoughness);
        float Gc = G_Smith(clearcoatNdotV, clearcoatNdotL, clearcoatRoughness);
        vec3 Fc = F_Schlick(vec3(0.04), max(dot(normal, viewDir), 0.0));
        
        vec3 clearcoatSpec = Dc * Gc * Fc / (4.0 * clearcoatNdotV * clearcoatNdotL + 0.001);
        finalColor = mix(finalColor, finalColor + clearcoatSpec * mainLightColor * clearcoatNdotL, clearcoat);
      }
      
      // Apply transmission if enabled
      if (transmission > 0.0) {
        // Simple approximation of transmission effect
        vec3 transmissionColor = baseColor * (1.0 - metalnessValue);
        finalColor = mix(finalColor, transmissionColor * ao, transmission);
      }
      
      // Apply tone mapping
      finalColor = toneMapping(finalColor);
      
      // Apply gamma correction
      finalColor = pow(finalColor, vec3(1.0 / 2.2));
      
      // Final output with alpha
      gl_FragColor = vec4(finalColor, alpha);
    }
  `
});

// Helper component to easily apply the shader to a GLB model
AFRAME.registerComponent('interior-enhanced-material', {
  schema: {
    // Expose shader parameters
    color: {type: 'color', default: '#ffffff'},
    roughness: {default: 0.5},
    metalness: {default: 0.3},
    ambientIntensity: {default: 0.5},
    lightIntensity: {default: 1.0},
    envMapIntensity: {default: 1.0},
    emissive: {type: 'color', default: '#000000'},
    emissiveIntensity: {default: 1.0},
    clearcoat: {default: 0.0},
    transmission: {default: 0.0},
    applyToModel: {default: true}
  },
  
  init: function() {
    // This will hold our environment map
    this.envMap = null;
    
    // Create env map - ideally this would be custom HDR for the space
    this.createEnvMap();
    
    if (this.data.applyToModel) {
      // Wait for model to load
      this.el.addEventListener('model-loaded', this.applyShader.bind(this));
    }
  },
  
  createEnvMap: function() {
    // Create a default environment map (ideally you'd load a real HDR)
    const scenEl = document.querySelector('a-scene');
    const cubeRenderTarget = new THREE.WebGLCubeRenderTarget(256);
    const cubeCamera = new THREE.CubeCamera(0.1, 1000, cubeRenderTarget);
    scenEl.object3D.add(cubeCamera);
    
    // Position it in the center of the scene
    cubeCamera.position.copy(this.el.object3D.position);
    cubeCamera.update(scenEl.renderer, scenEl.object3D);
    
    this.envMap = cubeRenderTarget.texture;
  },
  
  createMaterial: function(originalMaterial) {
    // Create new material with our shader
    const newMaterial = new THREE.ShaderMaterial({
      uniforms: {
        map: { value: originalMaterial.map },
        normalMap: { value: originalMaterial.normalMap },
        roughnessMap: { value: originalMaterial.roughnessMap },
        metalnessMap: { value: originalMaterial.metalnessMap },
        aoMap: { value: originalMaterial.aoMap },
        emissiveMap: { value: originalMaterial.emissiveMap },
        envMap: { value: this.envMap },
        
        color: { value: new THREE.Color(this.data.color) },
        roughness: { value: originalMaterial.roughness !== undefined ? 
                     originalMaterial.roughness : this.data.roughness },
        metalness: { value: originalMaterial.metalness !== undefined ? 
                     originalMaterial.metalness : this.data.metalness },
        
        emissive: { value: originalMaterial.emissive ? 
                   originalMaterial.emissive : new THREE.Color(this.data.emissive) },
        emissiveIntensity: { value: this.data.emissiveIntensity },
        
        lightIntensity: { value: this.data.lightIntensity },
        ambientLightColor: { value: new THREE.Color(0.4, 0.4, 0.5) },
        ambientIntensity: { value: this.data.ambientIntensity },
        
        envMapIntensity: { value: this.data.envMapIntensity },
        reflectivity: { value: 0.3 },
        
        aoMapIntensity: { value: originalMaterial.aoMapIntensity || 1.0 },
        normalScale: { value: originalMaterial.normalScale || 
                       new THREE.Vector2(1, 1) },
        
        transparent: { value: originalMaterial.transparent || false },
        opacity: { value: originalMaterial.opacity !== undefined ? 
                  originalMaterial.opacity : 1.0 },
        
        clearcoat: { value: this.data.clearcoat },
        clearcoatRoughness: { value: 0.1 },
        ior: { value: 1.5 },
        transmission: { value: this.data.transmission }
      },
      vertexShader: AFRAME.shaders['interior-enhanced'].vertexShader,
      fragmentShader: AFRAME.shaders['interior-enhanced'].fragmentShader
    });
    
    // Define USE_* constants based on available maps
    const defines = {};
    if (originalMaterial.map) defines.USE_MAP = '';
    if (originalMaterial.normalMap) defines.USE_NORMALMAP = '';
    if (originalMaterial.roughnessMap) defines.USE_ROUGHNESSMAP = '';
    if (originalMaterial.metalnessMap) defines.USE_METALNESSMAP = '';
    if (originalMaterial.aoMap) defines.USE_AOMAP = '';
    if (originalMaterial.emissiveMap) defines.USE_EMISSIVEMAP = '';
    if (this.envMap) defines.USE_ENVMAP = '';
    
    newMaterial.defines = defines;
    
    // Copy other material properties
    newMaterial.transparent = originalMaterial.transparent;
    newMaterial.side = originalMaterial.side;
    newMaterial.shadowSide = originalMaterial.shadowSide;
    
    return newMaterial;
  },
  
  applyShader: function(evt) {
    const model = evt.detail.model;
    
    // Apply the shader to each mesh in the model
    model.traverse(node => {
      if (node.isMesh && node.material) {
        // Handle materials array case
        if (Array.isArray(node.material)) {
          node.material = node.material.map(mat => this.createMaterial(mat));
        } else {
          // Handle single material case
          node.material = this.createMaterial(node.material);
        }
        
        // Set render properties
        node.castShadow = true;
        node.receiveShadow = true;
      }
    });
    
    console.log('Interior-enhanced shader applied to model');
  },
  
  // Material type mapping based on mesh name for special cases
  getMaterialType: function(meshName) {
    const name = meshName.toLowerCase();
    
    if (name.includes('floor')) {
      return {
        roughness: 0.7,
        metalness: 0.1,
        reflectivity: 0.3
      };
    } else if (name.includes('window') || name.includes('glass')) {
      return {
        roughness: 0.05,
        metalness: 0.0,
        transparent: true,
        opacity: 0.8,
        ior: 1.5,
        transmission: 0.9
      };
    } else if (name.includes('metal') || name.includes('chrome')) {
      return {
        roughness: 0.2,
        metalness: 0.9,
        reflectivity: 0.8
      };
    } else if (name.includes('wood')) {
      return {
        roughness: 0.6,
        metalness: 0.0,
        reflectivity: 0.2
      };
    } else if (name.includes('fabric') || name.includes('sofa') || name.includes('curtain')) {
      return {
        roughness: 0.9,
        metalness: 0.0,
        reflectivity: 0.1
      };
    }
    
    // Default values
    return {
      roughness: 0.5,
      metalness: 0.3,
      reflectivity: 0.3
    };
  }
});



  export const ModelComponent = {
    RenderOrder: AFRAME.components['render-order'],
    TransparentFix: AFRAME.components['transparent-fix'],
    CustomMaterial: AFRAME.components['custom-material'],
    fbxLoader6: AFRAME.components['fbx-model-loader-6'],
    fbxLoader5: AFRAME.components['fbx-model-loader-5'],
    grass: AFRAME.components['grass'],
    grass2: AFRAME.components['grass-2'],
    tree: AFRAME.components['random-tree-mesh'],
    rock: AFRAME.components['rock-generator'],
    shadow: AFRAME.components['shadow-material'],
    pbr: AFRAME.components['pbr-material'],
    interiorEnhanced: AFRAME.components['interior-enhanced-material']
  };
